import {
  formatDocumentVersionRef,
  type CollectionVersionRef,
  type DocumentVersionRef,
} from "../../shared/references/contentReferences";
import type { ApiSchema } from "../apiSchema";
import { requestJson, withQuery } from "../http/httpClient";
import { readArray, readInteger, readNullableString, readObject, readString, readStringRecord } from "../http/jsonDecoding";
import { readDocumentVersionRef, readSegmentTree } from "./contentDecoders";
import { listLeafSegments, type DocumentSegment } from "./documentSegments";
import { ANY_ATTACHED, attachedFilterQuery, readAttachedForms, type AttachedFilter, type AttachedForm } from "./searchApi";

/**
 * Source documents of a project's corpora (FR-CORPUS-1 to 4), read by `ID @ version`.
 * Routes: `server/src/dmw/api/routes/content/` (`corpora.py`, `references.py`).
 */

export type { DocumentSegment } from "./documentSegments";

export interface SourceDocumentSummary {
  readonly ref: DocumentVersionRef;
  /** The document's name from the import, such as `10300057`. */
  readonly key: string;
  /** The key as its corpus's key forms write it short, such as `3/57` (F136); `null` if none does. */
  readonly shortKey: string | null;
  /** What is attached to it, the most developed form first (F88). */
  readonly attached: readonly AttachedForm[];
}

export interface SourceDocumentListPage {
  readonly corpusVersion: CollectionVersionRef;
  readonly totalDocumentCount: number;
  readonly documents: readonly SourceDocumentSummary[];
}

export interface SourceDocument {
  readonly ref: DocumentVersionRef;
  /** The corpus that holds it: a start on it binds a version of this corpus (BA16-07). */
  readonly corpusId: string;
  readonly key: string;
  readonly metadata: Readonly<Record<string, string>>;
  readonly fullText: string;
  readonly segments: readonly DocumentSegment[];
}

/** One segment of an exact document version, opened by its segment reference. */
export interface SegmentVersion {
  /** `<item id>@<blob id>#<segment path>`. */
  readonly reference: string;
  readonly segmentType: string;
  readonly text: string;
}

export interface DocumentListQuery {
  readonly corpusVersion: CollectionVersionRef;
  readonly query?: string;
  /** Only the documents the filter keeps (F88); every document without one. */
  readonly attached?: AttachedFilter;
  /** The kind of segment the search is limited to: the filter's labels count only where they lie in it (F141). */
  readonly segmentType?: string | null;
  readonly order?: DocumentOrder;
  readonly offset: number;
  readonly limit: number;
}

/** `creation`: the order the corpus received them; `key`: by their keys. */
export type DocumentOrder = ApiSchema<"EntryOrder">;

/** One page of a corpus version's documents, each with what is attached to it. */
export function listSourceDocuments(query: DocumentListQuery, signal?: AbortSignal): Promise<SourceDocumentListPage> {
  const { corpusVersion, offset, limit } = query;
  return requestJson({
    method: "GET",
    path: withQuery(
      `/corpora/${encodeURIComponent(corpusVersion.collectionId)}/versions/${encodeURIComponent(corpusVersion.commit)}/documents`,
      {
        offset,
        limit,
        query: query.query ?? null,
        order: query.order ?? null,
        segment_type: query.segmentType ?? null,
        ...attachedFilterQuery(query.attached ?? ANY_ATTACHED),
      },
    ),
    signal,
    decodeResponse: (body) => {
      const listing = readObject(body, "document listing");
      return {
        corpusVersion,
        totalDocumentCount: readInteger(listing, "total_document_count"),
        documents: readArray(listing["documents"], "documents", (item) => {
          const entry = readObject(item, "document listing entry");
          return {
            ref: readDocumentVersionRef(entry, "reference"),
            key: readString(entry, "key"),
            shortKey: readNullableString(entry, "short_key"),
            attached: readAttachedForms(entry),
          };
        }),
      };
    },
  });
}

export function readSourceDocument(reference: DocumentVersionRef, signal?: AbortSignal): Promise<SourceDocument> {
  return requestJson({
    method: "GET",
    path: `/content/documents/${encodeURIComponent(formatDocumentVersionRef(reference))}`,
    signal,
    decodeResponse: (body) => {
      const document = readObject(body, "document");
      const fullText = readString(document, "text");
      return {
        ref: readDocumentVersionRef(document, "reference"),
        corpusId: readString(document, "corpus_id"),
        key: readString(document, "key"),
        metadata: readStringRecord(document, "metadata"),
        fullText,
        segments: listLeafSegments(fullText, readSegmentTree(document["segments"])),
      };
    },
  });
}

export function readSegment(
  reference: DocumentVersionRef,
  segmentPath: string,
  signal?: AbortSignal,
): Promise<SegmentVersion> {
  // The path's `/` separate its steps; the server reads the rest of the URL as the path.
  const encodedPath = segmentPath.split("/").map(encodeURIComponent).join("/");
  return requestJson({
    method: "GET",
    path: `/content/documents/${encodeURIComponent(formatDocumentVersionRef(reference))}/segments/${encodedPath}`,
    signal,
    decodeResponse: (body) => {
      const segment = readObject(body, "segment");
      return {
        reference: readString(segment, "reference"),
        segmentType: readString(segment, "segment_type"),
        text: readString(segment, "text"),
      };
    },
  });
}

/**
 * The latest version of a document named by its ID only: its version in the latest
 * version of the corpus that holds it. Needs `read_project` in that corpus's project.
 */
export function findLatestDocumentVersion(documentId: string, signal?: AbortSignal): Promise<DocumentVersionRef> {
  return requestJson({
    method: "GET",
    path: `/content/documents/${encodeURIComponent(documentId)}/latest`,
    signal,
    decodeResponse: (body) => readDocumentVersionRef(readObject(body, "latest document version"), "reference"),
  });
}

/**
 * A document chosen at random from the `totalDocumentCount` documents of a corpus version that
 * `kept` keeps: the same population the count was taken from.
 */
export async function pickRandomDocument(
  corpusVersion: CollectionVersionRef,
  totalDocumentCount: number,
  kept: Pick<DocumentListQuery, "attached" | "segmentType">,
): Promise<SourceDocumentSummary | null> {
  if (totalDocumentCount === 0) {
    return null;
  }
  const offset = Math.floor(Math.random() * totalDocumentCount);
  const page = await listSourceDocuments({ corpusVersion, attached: kept.attached, segmentType: kept.segmentType, offset, limit: 1 });
  return page.documents[0] ?? null;
}
